import React, { useState, useEffect, useCallback } from 'react';
import { C } from '../styles/theme';
import { parseCsv, downloadCsv } from './bulkImport/csvUtils';
import { recipeImportService } from '../services/api';

const FONT = "'Plus Jakarta Sans', sans-serif";

// The columns a user fills in by hand. Export adds Product Code and
// Ingredient Code, which import honours when present.
const TEMPLATE_HEADERS = [
    'Product Name', 'Ingredient Name', 'Quantity', 'Unit', 'Dining Option',
];

const EXPORT_HEADERS = [
    'Product Code', 'Product Name', 'Ingredient Code', 'Ingredient Name',
    'Quantity', 'Unit', 'Dining Option',
];

const REFERENCE_HEADERS = ['Type', 'Name', 'Code', 'Accepted Units'];

const EXAMPLE_ROWS = [
    {
        'Product Name': 'SMASH', 'Ingredient Name': 'Beef Patty',
        Quantity: '150', Unit: 'grams', 'Dining Option': 'all',
    },
    {
        'Product Name': 'SMASH', 'Ingredient Name': 'Burger Bun',
        Quantity: '1', Unit: 'pieces', 'Dining Option': 'all',
    },
    {
        'Product Name': 'SMASH', 'Ingredient Name': 'Burger Box',
        Quantity: '1', Unit: 'pieces', 'Dining Option': 'delivery',
    },
];

const STATUS_STYLES = {
    new: { bg: C.successLight, fg: C.success },
    changed: { bg: C.amberLight, fg: C.amber },
    unchanged: { bg: C.faint, fg: C.muted },
};

const btn = (variant = 'ghost') => ({
    padding: '10px 16px',
    borderRadius: 8,
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    border: variant === 'primary' ? 'none' : `1px solid ${C.border}`,
    background: variant === 'primary' ? C.green : C.white,
    color: variant === 'primary' ? C.white : C.text,
});

const th = {
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: C.muted,
    padding: 8,
};

const LINE_STYLES = {
    added: { fg: C.success, sign: '+' },
    removed: { fg: C.red, sign: '−' },
    modified: { fg: C.amber, sign: '~' },
    unchanged: { fg: C.muted, sign: '' },
};

// One ingredient line inside an expanded product. For a modified line the
// old value is struck through next to the new one — the whole point of the
// confirmation step is seeing what is about to be overwritten.
const DiffLine = ({ line, t }) => {
    const s = LINE_STYLES[line.change] || LINE_STYLES.unchanged;
    const qty = (v) => `${v} ${line.unit}`.trim();
    return (
        <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8,
            padding: '4px 0 4px 24px', fontSize: 12,
        }}>
            <span style={{ width: 12, color: s.fg, fontWeight: 700 }}>{s.sign}</span>
            <span style={{ flex: 1, color: C.text }}>{line.ingredient_name}</span>
            <span style={{ color: C.muted, minWidth: 120 }}>
                {line.dining_option === 'all'
                    ? t('all_options')
                    : (line.dining_option_label || line.dining_option)}
            </span>
            <span style={{ minWidth: 140, textAlign: 'right', color: s.fg }}>
                {line.change === 'modified' && (
                    <span style={{
                        textDecoration: 'line-through', color: C.muted,
                        marginInlineEnd: 6,
                    }}>
                        {qty(line.previous_quantity)}
                    </span>
                )}
                {line.change === 'removed'
                    ? <span style={{ textDecoration: 'line-through' }}>
                        {qty(line.previous_quantity)}
                      </span>
                    : qty(line.quantity_needed)}
            </span>
        </div>
    );
};

const RecipeImportModal = ({ open, onClose, onImported, t }) => {
    const [stage, setStage] = useState('upload');
    const [rows, setRows] = useState([]);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState({});

    const reset = useCallback(() => {
        setStage('upload');
        setRows([]);
        setPreview(null);
        setError('');
        setBusy(false);
        setExpanded({});
    }, []);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, handleClose]);

    if (!open) return null;

    const runPreview = async (candidateRows) => {
        setBusy(true);
        setError('');
        try {
            const result = await recipeImportService.preview(candidateRows);
            setPreview(result);
            setStage('preview');
        } catch (e) {
            setError(t('recipe_import_failed'));
        } finally {
            setBusy(false);
        }
    };

    const handleFile = async (file) => {
        try {
            const text = await file.text();
            const parsed = parseCsv(text);
            if (parsed.rows.length === 0) {
                setError(t('recipe_import_empty_file'));
                return;
            }
            setRows(parsed.rows);
            await runPreview(parsed.rows);
        } catch (e) {
            setError(t('recipe_import_unreadable_file'));
        }
    };

    const handleConfirm = async () => {
        setBusy(true);
        setError('');
        try {
            const result = await recipeImportService.apply(rows);
            setPreview(p => ({ ...p, applied: result.applied, skipped: result.skipped }));
            setStage('done');
            if (onImported) onImported();
        } catch (e) {
            setError(t('recipe_import_failed'));
        } finally {
            setBusy(false);
        }
    };

    const handleReference = async () => {
        setBusy(true);
        setError('');
        try {
            const data = await recipeImportService.reference();
            downloadCsv('aqana_names_reference.csv', REFERENCE_HEADERS, data.rows);
        } catch (e) {
            setError(t('names_reference_failed'));
        } finally {
            setBusy(false);
        }
    };

    const handleExport = async () => {
        setBusy(true);
        setError('');
        try {
            const data = await recipeImportService.export();
            if (!data.rows || data.rows.length === 0) {
                setError(t('no_recipes_to_export'));
                return;
            }
            downloadCsv('aqana_recipes.csv', EXPORT_HEADERS, data.rows);
        } catch (e) {
            setError(t('recipe_export_failed'));
        } finally {
            setBusy(false);
        }
    };

    const blocked = !preview
        || preview.errors.length > 0
        || preview.missing_ingredients.length > 0;

    const nothingToDo = preview
        && preview.summary.new === 0
        && preview.summary.changed === 0;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={t('import_recipes')}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1000, fontFamily: FONT, padding: 16,
            }}
        >
            <div style={{
                background: C.white, borderRadius: 12,
                width: 'min(880px, 100%)', maxHeight: '86vh',
                overflowY: 'auto', padding: 24,
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 16,
                }}>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>
                        {t('import_recipes')}
                    </h2>
                    <button
                        onClick={handleClose}
                        aria-label={t('close')}
                        style={{
                            border: 'none', background: 'none', fontSize: 24,
                            lineHeight: 1, cursor: 'pointer', color: C.muted,
                        }}
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <div style={{
                        background: C.redLight, color: C.redText,
                        border: `1px solid ${C.redBorder}`,
                        padding: 12, borderRadius: 8, marginBottom: 16,
                        fontSize: 13,
                    }}>
                        {error}
                    </div>
                )}

                {stage === 'upload' && (
                    <div>
                        <p style={{ fontSize: 13, color: C.slate, marginTop: 0 }}>
                            {t('recipe_import_instructions')}
                        </p>
                        <p style={{ fontSize: 12, color: C.muted, marginTop: -6 }}>
                            {t('names_reference_hint')}
                        </p>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                            <button
                                style={btn()}
                                onClick={() => downloadCsv(
                                    'aqana_recipe_template.csv',
                                    TEMPLATE_HEADERS,
                                    EXAMPLE_ROWS,
                                )}
                            >
                                {t('download_template')}
                            </button>
                            <button style={btn()} onClick={handleReference} disabled={busy}>
                                {t('download_names_reference')}
                            </button>
                            <button style={btn()} onClick={handleExport} disabled={busy}>
                                {t('export_current_recipes')}
                            </button>
                        </div>
                        <label style={{
                            display: 'block', border: `1px dashed ${C.border}`,
                            borderRadius: 8, padding: 20, textAlign: 'center',
                            cursor: 'pointer', fontSize: 13, color: C.muted,
                        }}>
                            {t('choose_csv_file')}
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                style={{ display: 'block', margin: '12px auto 0' }}
                                onChange={(e) => {
                                    const file = e.target.files && e.target.files[0];
                                    // Clear the input so re-picking the same
                                    // file after Back still fires onChange.
                                    e.target.value = '';
                                    if (file) handleFile(file);
                                }}
                            />
                        </label>
                    </div>
                )}

                {stage === 'preview' && preview && (
                    <div>
                        <p style={{ fontSize: 13, color: C.slate, marginTop: 0 }}>
                            {t('recipe_import_summary')
                                .replace('{new}', preview.summary.new)
                                .replace('{changed}', preview.summary.changed)
                                .replace('{unchanged}', preview.summary.unchanged)}
                        </p>

                        {preview.missing_ingredients.length > 0 && (
                            <div style={{
                                background: C.redLight, border: `1px solid ${C.redBorder}`,
                                padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13,
                            }}>
                                <strong style={{ color: C.redText }}>
                                    {t('missing_ingredients')}
                                </strong>
                                <ul style={{ margin: '8px 0', paddingInlineStart: 20 }}>
                                    {preview.missing_ingredients.map(m => (
                                        <li key={m.name}>
                                            {m.name} — {t('row')} {m.row_numbers.join(', ')}
                                        </li>
                                    ))}
                                </ul>
                                <p style={{ margin: 0, fontSize: 12, color: C.muted }}>
                                    {t('create_missing_ingredients_first')}
                                </p>
                            </div>
                        )}

                        {preview.errors.length > 0 && (
                            <div style={{
                                background: C.redLight, border: `1px solid ${C.redBorder}`,
                                padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13,
                            }}>
                                <strong style={{ color: C.redText }}>{t('errors')}</strong>
                                <ul style={{ margin: '8px 0 0', paddingInlineStart: 20 }}>
                                    {preview.errors.map((err, i) => (
                                        <li key={`${err.row_number}-${i}`}>
                                            {t('row')} {err.row_number}: {err.message}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {preview.diff.length === 0 ? (
                            <p style={{ fontSize: 13, color: C.muted }}>
                                {t('no_recipes_in_file')}
                            </p>
                        ) : (
                            <div>
                                <div style={{
                                    display: 'flex', ...th, padding: '8px 8px 8px 28px',
                                }}>
                                    <span style={{ flex: 1 }}>{t('product')}</span>
                                    <span style={{ width: 120 }}>{t('status')}</span>
                                    <span style={{ width: 90, textAlign: 'right' }}>
                                        {t('ingredients')}
                                    </span>
                                </div>
                                {preview.diff.map(d => {
                                    const s = STATUS_STYLES[d.status] || STATUS_STYLES.unchanged;
                                    const isOpen = !!expanded[d.product_code];
                                    // Unchanged products have nothing worth
                                    // reading, so don't invite a click.
                                    const canExpand = d.status !== 'unchanged'
                                        && (d.lines || []).length > 0;
                                    return (
                                        <div key={d.product_code}
                                            style={{ borderTop: `1px solid ${C.border}` }}>
                                            <button
                                                type="button"
                                                onClick={() => canExpand && setExpanded(e => ({
                                                    ...e, [d.product_code]: !e[d.product_code],
                                                }))}
                                                aria-expanded={isOpen}
                                                disabled={!canExpand}
                                                style={{
                                                    display: 'flex', alignItems: 'center',
                                                    width: '100%', padding: 8,
                                                    background: 'none', border: 'none',
                                                    fontFamily: FONT, textAlign: 'inherit',
                                                    cursor: canExpand ? 'pointer' : 'default',
                                                }}
                                            >
                                                <span style={{
                                                    width: 20, color: C.muted, fontSize: 11,
                                                    visibility: canExpand ? 'visible' : 'hidden',
                                                }}>
                                                    {isOpen ? '▼' : '▶'}
                                                </span>
                                                <span style={{
                                                    flex: 1, fontSize: 13, color: C.text,
                                                    fontWeight: 600,
                                                }}>
                                                    {d.product_name || d.product_code}
                                                    <span style={{
                                                        color: C.mutedLight, fontWeight: 400,
                                                        marginInlineStart: 8, fontSize: 12,
                                                    }}>
                                                        {d.product_code}
                                                    </span>
                                                </span>
                                                <span style={{ width: 120 }}>
                                                    <span style={{
                                                        background: s.bg, color: s.fg,
                                                        padding: '2px 8px', borderRadius: 999,
                                                        fontSize: 11, fontWeight: 700,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.6,
                                                    }}>
                                                        {t(`recipe_status_${d.status}`)}
                                                    </span>
                                                </span>
                                                <span style={{
                                                    width: 90, textAlign: 'right',
                                                    fontSize: 13, color: C.slate,
                                                }}>
                                                    {d.ingredient_count ?? d.ingredients.length}
                                                </span>
                                            </button>
                                            {isOpen && (
                                                <div style={{
                                                    paddingBottom: 8, background: C.faint,
                                                }}>
                                                    {d.lines.map((line, i) => (
                                                        <DiffLine
                                                            key={`${line.inventory_id}-${line.dining_option}-${i}`}
                                                            line={line}
                                                            t={t}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div style={{
                            display: 'flex', gap: 12, marginTop: 20,
                            justifyContent: 'flex-end',
                        }}>
                            <button style={btn()} onClick={reset} disabled={busy}>
                                {t('back')}
                            </button>
                            <button
                                style={{
                                    ...btn('primary'),
                                    background: (blocked || nothingToDo) ? C.mutedLight : C.green,
                                    cursor: (blocked || nothingToDo) ? 'not-allowed' : 'pointer',
                                }}
                                onClick={handleConfirm}
                                disabled={busy || blocked || nothingToDo}
                            >
                                {t('confirm_import')}
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'done' && (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <p style={{ fontSize: 15, fontWeight: 700, color: C.greenDark }}>
                            {t('recipes_imported')}
                        </p>
                        <p style={{ fontSize: 13, color: C.muted }}>
                            {t('recipe_import_result')
                                .replace('{applied}', preview?.applied ?? 0)
                                .replace('{skipped}', preview?.skipped ?? 0)}
                        </p>
                        <button style={btn('primary')} onClick={handleClose}>
                            {t('done')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecipeImportModal;
